import { useManager } from 'dreamstate';
import React, { useCallback, useState, FocusEvent, LegacyRef, ReactElement } from 'react';

import { IconButton, Menu, MenuButton, MenuItem, MenuList, rem } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { BokehTooltip } from '@vimeo/live-core/components/bokeh';
import { LiveErrorBoundary } from '@vimeo/live-core/components/error/boundary';
import { useControlledPopover } from '@vimeo/live-core/hooks/dom/useControlledPopover';
import { PollManager, IPollContext } from '@vimeo/live-core/store/firebase/interaction/poll';

import { createInteractionDomName } from '../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../translations';
import { DownloadModal } from '../shared/DownloadModal';
import { InteractionToolsSubmenu } from '../shared/InteractionToolsSubmenu';

interface IPollSettingsButtonProps extends IBaseDomElementProps {
  withInteractionSubmenu?: boolean;
  buttonIcon: ReactElement;
  pollContext?: IPollContext;
}

export function PollSettingsButton({
  id = createInteractionDomName('poll-settings-button'),
  className = createInteractionDomName('poll-settings-button'),
  withInteractionSubmenu = false,
  buttonIcon,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  pollContext: { pollsHistory } = useManager(PollManager, ({ pollsHistory }) => [pollsHistory]),
}: IPollSettingsButtonProps) {
  const { triggerRef, contentRef, isOpen, onOpenPopover, onClosePopover } = useControlledPopover();

  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const onClickCloseDownloadModal = useCallback(() => setIsDownloadOpen(false), []);

  const onDownloadClick = useCallback(() => {
    setIsDownloadOpen(true);
    onClosePopover();
  }, [onClosePopover]);

  const onClickDownloadModal = useCallback(() => {
    // todo: Track polls, wait Laura.

    setIsDownloadOpen(false);
  }, []);

  const onFocus = useCallback((event: FocusEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <LiveErrorBoundary component={'PollSettingsButton'} isDetailed={false}>
      <Menu
        isOpen={isOpen}
        placement={'bottom'}
        closeOnSelect={false}
        onOpen={onOpenPopover}
        onClose={onClosePopover}
      >
        <BokehTooltip label={isOpen ? null : translations.settings} placement={'bottom'}>
          <MenuButton
            as={IconButton}
            ref={triggerRef}
            id={id}
            className={className}
            aria-label={'settings'}
            icon={buttonIcon}
            size={'sm'}
            variant={'minimal'}
            onFocus={onFocus}
          />
        </BokehTooltip>

        <MenuList
          ref={contentRef as LegacyRef<HTMLDivElement>}
          overflow={'unset'}
          width={rem(240)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: rem(4),
          }}
        >
          <MenuItem onClick={onDownloadClick}>{translations.download}</MenuItem>

          {withInteractionSubmenu ? (
            <InteractionToolsSubmenu placement={'right-start'} onCloseParentMenu={onClosePopover} />
          ) : null}
        </MenuList>
      </Menu>

      <DownloadModal
        header={translations.download}
        prefix={'export_poll'}
        history={pollsHistory}
        isOpen={isDownloadOpen}
        onClose={onClickCloseDownloadModal}
        onDownload={onClickDownloadModal}
      />
    </LiveErrorBoundary>
  );
}
